/* ===========================================================
   K18 — Product Carousel (Collections + Single Feature)
   Shortcodes:
     [collection-carousel="collection-handle"]
     [product-feature="product-handle"]
   - Minimal black/white design, 1px black borders, rounded corners, no shadows
   - Collection: price + "View Product" button (no Add To Cart)
   - Feature: image left, details right; CTA overlay on image (View Product + Add To Cart)
   - Works when article content is injected late (MutationObserver), and waits for Swiper
   =========================================================== */

(function () {
  const ARTICLE_SELECTOR =
    '.article-template, .article__content-inner, .article-template__content, .article-template__content.rte, .blog__article__content.rte, .article .rte, .article-content, .rte, .elementor-widget-theme-post-content, rte-formatter';

  const HAS_SHORTCODE = /\[(?:collection-carousel|product-feature)="[^"]+"\]/;

  // Run now, on DOMContentLoaded or load, then observe mutations
  ready(initWithObserver);

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
    window.addEventListener('load', fn, { once: true });
  }

  function initWithObserver() {
    processAll();
    const body = document.body;
    if (!body) return;
    if (HAS_SHORTCODE.test(body.textContent || '')) {
      const mo = new MutationObserver(() => {
        processAll();
        if (!HAS_SHORTCODE.test(body.textContent || '')) mo.disconnect();
      });
      mo.observe(body, { childList: true, subtree: true });
    }
  }

  function processAll() {
    const container = document.querySelector(ARTICLE_SELECTOR);
    if (!container) return false;

    let changed = false;
    let idxC = 0, idxP = 0;

    // Inject collection wrappers
    container.innerHTML = container.innerHTML.replace(
      /\[collection-carousel="([^"]+)"\]/g,
      (_, handle) => {
        changed = true;
        const id = `k18-carousel-${handle}-${idxC++}`;
        return `
          <section id="${id}" class="k18-slider-wrapper k18-slider" data-collection="${handle}">
            <div class="swiper-container"><div class="swiper-wrapper"></div></div>
          </section>`;
      }
    );

    // Inject product feature wrappers
    container.innerHTML = container.innerHTML.replace(
      /\[product-feature="([^"]+)"\]/g,
      (_, handle) => {
        changed = true;
        const id = `k18-feature-${handle}-${idxP++}`;
        return `
          <section id="${id}" class="k18-feature-wrapper" data-product="${handle}">
            <div class="k18-feature">
              <div class="k18-feature__media">
                <div class="k18-feature__img k18-skel"></div>
                <div class="k18-feature__cta-overlay">
                  <a class="k18-btn k18-btn--ghost k18-feature__view" href="#" target="_self" rel="noopener">View Product</a>
                  <form class="k18-feature__purchase" action="/cart/add" method="post">
                    <input type="hidden" name="id" value="">
                    <button type="submit" class="k18-btn k18-btn--primary">Add To Cart</button>
                  </form>
                </div>
              </div>
              <div class="k18-feature__details">
                <h2 class="k18-feature__title k18-skel">Loading…</h2>
                <div class="k18-feature__desc k18-skel"></div>
                <div class="k18-feature__price-row">
                  <span class="k18-feature__price k18-skel">$—</span>
                </div>
                <div class="k18-feature__variant-label">Variant</div>
                <div class="k18-feature__variant-grid" role="listbox" aria-label="Select variant"></div>
              </div>
            </div>
          </section>`;
      }
    );

    // Build all wrappers we just inserted
    container.querySelectorAll('.k18-slider-wrapper[data-collection]').forEach(buildCollection);
    container.querySelectorAll('.k18-feature-wrapper[data-product]').forEach(buildFeature);
    return changed;
  }

  /* ===================== COLLECTIONS ===================== */
  function buildCollection(wrapper) {
    if (wrapper.__built) return;
    wrapper.__built = true;

    const handle   = wrapper.getAttribute('data-collection');
    const trackEl  = wrapper.querySelector('.swiper-wrapper');
    const swiperEl = wrapper.querySelector('.swiper-container');

    fetch(`/collections/${handle}/products.json?limit=12`)
      .then(r => r.json())
      .then(json => {
        (json.products || []).forEach(product => {
          const variant = product.variants && product.variants[0];
          if (!variant) return;

          const imgSrc   = getImageFromCollection(product.images); // objects [{src}]
          const priceTxt = formatPrice(variant.price);

          const slide = document.createElement('div');
          slide.className = 'swiper-slide';
          if (imgSrc) slide.style.backgroundImage = `url(${imgSrc})`;

          // Per brief: include price + "View Product" (no Add To Cart) on carousel
          slide.innerHTML = `
            <div class="product-name">${escapeHtml(product.title)}</div>
            <div class="product-price">${priceTxt}</div>
            <a class="k18-view-btn" href="/products/${product.handle}" aria-label="View ${escapeHtml(product.title)}">View Product</a>
          `;
          trackEl.appendChild(slide);
        });

        waitForSwiper(() => {
          new Swiper(swiperEl, {
            freeMode: true,
            slidesPerView: 'auto',
            spaceBetween: 12,
            loop: false,
            navigation: false,
            pagination: false
          });
        });
      })
      .catch(err => console.error('K18 collection carousel error:', err));
  }

  /* ===================== FEATURE ===================== */
  function buildFeature(wrapper) {
    if (wrapper.__built) return;
    wrapper.__built = true;

    const handle  = wrapper.getAttribute('data-product');
    const card    = wrapper.querySelector('.k18-feature');

    const imgBox  = card.querySelector('.k18-feature__img');
    const titleEl = card.querySelector('.k18-feature__title');
    const descEl  = card.querySelector('.k18-feature__desc');
    const priceEl = card.querySelector('.k18-feature__price');
    const gridEl  = card.querySelector('.k18-feature__variant-grid');
    const labelEl = card.querySelector('.k18-feature__variant-label');
    const form    = card.querySelector('.k18-feature__purchase');
    const idInput = form.querySelector('input[name="id"]');
    const viewA   = card.querySelector('.k18-feature__view');

    viewA.href = `/products/${handle}`;

    fetch(`/products/${handle}.js`)
      .then(r => r.json())
      .then(prod => {
        if (!prod || !Array.isArray(prod.variants) || prod.variants.length === 0) {
          titleEl.textContent = 'Product unavailable';
          card.classList.add('k18-feature--error');
          return;
        }

        // /products/{handle}.js returns array of string URLs
        const heroImg =
          (Array.isArray(prod.images) && (prod.images[1] || prod.images[0])) ||
          prod.featured_image || '';
        if (heroImg) {
          imgBox.classList.remove('k18-skel');
          imgBox.style.backgroundImage = `url("${heroImg}")`;
        }

        titleEl.classList.remove('k18-skel');
        titleEl.textContent = prod.title;

        const clean = stripHtml(prod.description || '');
        const short = clean.length > 280 ? clean.slice(0, 277) + '…' : clean;
        descEl.classList.remove('k18-skel');
        descEl.textContent = short;

        const variants = prod.variants;
        const firstVar = variants[0];
        idInput.value  = firstVar.id;
        priceEl.classList.remove('k18-skel');
        priceEl.textContent = formatPrice(firstVar.price);

        // If there’s only one "Default" variant, hide label+grid (no selector)
        const singleDefault =
          variants.length === 1 &&
          (firstVar.title === 'Default Title' || firstVar.title === 'Default');

        if (singleDefault) {
          if (labelEl) labelEl.style.display = 'none';
          if (gridEl)  gridEl.style.display  = 'none';
          card.classList.add('k18-feature--compact');
          return;
        }

        // Otherwise build variant pills
        const btns = variants.map(v => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'k18-var-btn';
          b.textContent = v.title;
          b.dataset.id = v.id;
          b.dataset.price = v.price;
          b.setAttribute('role', 'option');
          b.setAttribute('aria-label', `Select ${v.title}`);
          return b;
        });
        btns.forEach(b => gridEl.appendChild(b));

        function selectVariant(btn) {
          btns.forEach(x => x.classList.remove('is-active'));
          btn.classList.add('is-active');
          idInput.value = btn.dataset.id;
          priceEl.textContent = formatPrice(btn.dataset.price);
        }
        selectVariant(btns[0]);
        btns.forEach(b => b.addEventListener('click', () => selectVariant(b)));
      })
      .catch(err => {
        titleEl.textContent = 'Error loading product';
        console.error('K18 feature error:', err);
      });
  }

  /* ===================== Helpers ===================== */

  function waitForSwiper(cb) {
    if (window.Swiper) return cb();
    const iv = setInterval(() => { if (window.Swiper) { clearInterval(iv); cb(); } }, 50);
    setTimeout(() => clearInterval(iv), 3000);
  }

  // collection endpoint => images: [{src: "..."}]
  function getImageFromCollection(images) {
    if (!images || !images.length) return '';
    const first  = images[0] && images[0].src;
    const second = images[1] && images[1].src;
    return first || second || '';
  }

  // cents number or string dollars -> AUD locale with separators
  function formatPrice(raw) {
    let dollars;
    if (typeof raw === 'number') dollars = raw / 100;
    else {
      const s = String(raw).trim();
      if (/^\d+$/.test(s)) dollars = parseInt(s, 10) / 100;
      else if (/^\d+(\.\d+)?$/.test(s)) dollars = parseFloat(s);
      else dollars = 0;
    }
    return dollars.toLocaleString(undefined, {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
})();
